import React, { useState, useEffect } from 'react';
import {
  FiBook, FiPlus, FiEdit2, FiTrash2, FiSearch, FiRefreshCw, FiX, FiCheck,
  FiChevronLeft, FiChevronRight, FiEye, FiFileText, FiTag, FiImage
} from 'react-icons/fi';
import { helpCenterPublicApi, helpCenterAdminApi } from '../services/helpCenterApi';
import { HelpCategory, HelpArticle } from '../types/helpCenter';
import './HelpCenter.css';

const Icon = ({ icon: IconComponent, className }: { icon: any; className?: string }) =>
  React.createElement(IconComponent as any, { className });

const HelpCenter: React.FC = () => {
  const [activeView, setActiveView] = useState<'categories' | 'articles'>('categories');
  const [categories, setCategories] = useState<HelpCategory[]>([]);
  const [articles, setArticles] = useState<HelpArticle[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modals
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showArticleModal, setShowArticleModal] = useState(false);
  const [showArticleView, setShowArticleView] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<HelpCategory | null>(null);
  const [selectedArticle, setSelectedArticle] = useState<HelpArticle | null>(null);
  const [editingCategory, setEditingCategory] = useState<HelpCategory | null>(null);
  const [editingArticle, setEditingArticle] = useState<HelpArticle | null>(null);
  
  // Forms
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    nameAr: '',
    description: '',
    descriptionAr: '',
    order: 0,
    isPublished: true,
  });
  
  const [articleForm, setArticleForm] = useState({
    title: '',
    titleAr: '',
    content: '',
    summary: '',
    categoryId: 0,
    tags: '',
    isPublished: true,
    isFeatured: false,
  });

  useEffect(() => {
    loadData();
  }, [activeView, currentPage]);

  const loadData = async () => {
    try {
      setLoading(true);
      if (activeView === 'categories') {
        const data = await helpCenterAdminApi.getAllCategories();
        setCategories(data);
      } else {
        const data = await helpCenterAdminApi.getAllArticles(currentPage, 20, undefined, undefined, searchTerm);
        setArticles(data.content);
        setTotalPages(data.totalPages);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCategory = async () => {
    try {
      await helpCenterAdminApi.createCategory(categoryForm);
      setShowCategoryModal(false);
      resetCategoryForm();
      loadData();
    } catch (error: any) {
      alert(`Error: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleUpdateCategory = async () => {
    if (!editingCategory) return;
    try {
      await helpCenterAdminApi.updateCategory(editingCategory.id, categoryForm);
      setShowCategoryModal(false);
      setEditingCategory(null);
      resetCategoryForm();
      loadData();
    } catch (error: any) {
      alert(`Error: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleDeleteCategory = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    try {
      await helpCenterAdminApi.deleteCategory(id);
      loadData();
    } catch (error: any) {
      alert(`Error: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleCreateArticle = async () => {
    // Validate required fields
    if (!articleForm.title || !articleForm.titleAr || !articleForm.content) {
      alert('Please fill in all required fields (Title English, Title Arabic, and Content)');
      return;
    }
    
    // Validate categoryId - must be a valid number > 0
    const categoryId = articleForm.categoryId ? Number(articleForm.categoryId) : 0;
    if (!categoryId || categoryId === 0 || isNaN(categoryId)) {
      alert('Please select a valid category');
      return;
    }
    
    try {
      const tagsArray = articleForm.tags
        ? articleForm.tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0)
        : [];
      
      // Build article data explicitly - ensure categoryId is definitely a number
      const articleData: any = {
        title: articleForm.title.trim(),
        titleAr: articleForm.titleAr.trim(),
        content: articleForm.content.trim(),
        categoryId: Number(categoryId), // Force conversion to number
      };
      
      // Add optional fields only if they have values
      if (articleForm.summary && articleForm.summary.trim()) {
        articleData.summary = articleForm.summary.trim();
      }
      if (tagsArray.length > 0) {
        articleData.tags = tagsArray;
      }
      if (articleForm.isPublished !== undefined) {
        articleData.isPublished = Boolean(articleForm.isPublished);
      }
      if (articleForm.isFeatured !== undefined) {
        articleData.isFeatured = Boolean(articleForm.isFeatured);
      }
      
      console.log('Creating article with data:', JSON.stringify(articleData, null, 2));
      console.log('CategoryId:', articleData.categoryId, 'Type:', typeof articleData.categoryId, 'IsNaN:', isNaN(articleData.categoryId));
      
      await helpCenterAdminApi.createArticle(articleData);
      setShowArticleModal(false);
      resetArticleForm();
      loadData();
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          error.message || 
                          'Failed to create article';
      console.error('Create article error:', error.response?.data);
      console.error('Request data that failed:', {
        title: articleForm.title,
        titleAr: articleForm.titleAr,
        categoryId: articleForm.categoryId,
        categoryIdType: typeof articleForm.categoryId,
      });
      alert(`Error: ${errorMessage}`);
    }
  };

  const handleUpdateArticle = async () => {
    if (!editingArticle) return;
    
    // Validate required fields
    if (!articleForm.title || !articleForm.titleAr || !articleForm.content) {
      alert('Please fill in all required fields (Title English, Title Arabic, and Content)');
      return;
    }
    
    if (!articleForm.categoryId || articleForm.categoryId === 0) {
      alert('Please select a category');
      return;
    }
    
    try {
      const tagsArray = articleForm.tags
        ? articleForm.tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0)
        : [];
      
      const articleData: any = {
        title: articleForm.title.trim(),
        titleAr: articleForm.titleAr.trim(),
        content: articleForm.content.trim(),
        categoryId: Number(articleForm.categoryId), // Ensure it's a number
        tags: tagsArray,
        isPublished: articleForm.isPublished,
        isFeatured: articleForm.isFeatured,
      };
      
      // Add optional fields only if they have values
      if (articleForm.summary && articleForm.summary.trim()) {
        articleData.summary = articleForm.summary.trim();
      }
      
      await helpCenterAdminApi.updateArticle(editingArticle.id, articleData);
      setShowArticleModal(false);
      setEditingArticle(null);
      resetArticleForm();
      loadData();
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          error.message || 
                          'Failed to update article';
      console.error('Update article error:', error.response?.data);
      alert(`Error: ${errorMessage}`);
    }
  };

  const handleDeleteArticle = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this article?')) return;
    try {
      await helpCenterAdminApi.deleteArticle(id);
      loadData();
    } catch (error: any) {
      alert(`Error: ${error.response?.data?.message || error.message}`);
    }
  };

  const openEditCategory = (category: HelpCategory) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name,
      nameAr: category.nameAr,
      description: category.description || '',
      descriptionAr: category.descriptionAr || '',
      order: category.order,
      isPublished: category.isPublished,
    });
    setShowCategoryModal(true);
  };

  const openEditArticle = (article: HelpArticle) => {
    setEditingArticle(article);
    setArticleForm({
      title: article.title,
      titleAr: article.titleAr,
      content: article.content,
      summary: article.summary || '',
      categoryId: article.category?.id || 0,
      tags: article.tags && article.tags.length > 0 ? article.tags.join(', ') : '',
      isPublished: article.isPublished,
      isFeatured: article.isFeatured,
    });
    setShowArticleModal(true);
  };

  const resetCategoryForm = () => {
    setCategoryForm({
      name: '',
      nameAr: '',
      description: '',
      descriptionAr: '',
      order: 0,
      isPublished: true,
    });
    setEditingCategory(null);
  };

  const resetArticleForm = () => {
    setArticleForm({
      title: '',
      titleAr: '',
      content: '',
      summary: '',
      categoryId: 0,
      tags: '',
      isPublished: true,
      isFeatured: false,
    });
    setEditingArticle(null);
  };

  return (
    <div className="help-center">
      <div className="hc-header">
        <div className="hc-header-left">
          <Icon icon={FiBook} className="hc-header-icon" />
          <div>
            <h2>Help Center</h2>
            <p>Manage help articles and categories</p>
          </div>
        </div>
        <button className="btn-refresh" onClick={loadData} disabled={loading}>
          <Icon icon={FiRefreshCw} className={loading ? 'spinning' : ''} />
        </button>
      </div>

      <div className="hc-tabs">
        <button
          className={`hc-tab ${activeView === 'categories' ? 'active' : ''}`}
          onClick={() => setActiveView('categories')}
        >
          <Icon icon={FiTag} /> Categories ({categories.length})
        </button>
        <button
          className={`hc-tab ${activeView === 'articles' ? 'active' : ''}`}
          onClick={() => setActiveView('articles')}
        >
          <Icon icon={FiFileText} /> Articles ({articles.length})
        </button>
      </div>

      <div className="hc-content">
        {activeView === 'categories' ? (
          <div className="hc-section">
            <div className="hc-section-header">
              <h3>Categories</h3>
              <button className="btn-primary" onClick={() => { resetCategoryForm(); setShowCategoryModal(true); }}>
                <Icon icon={FiPlus} /> Create Category
              </button>
            </div>

            {loading ? (
              <div className="loading-state">
                <Icon icon={FiRefreshCw} className="spinning" />
                <p>Loading categories...</p>
              </div>
            ) : categories.length === 0 ? (
              <div className="empty-state">
                <Icon icon={FiBook} />
                <p>No categories found</p>
              </div>
            ) : (
              <div className="hc-categories-grid">
                {categories.map(cat => (
                  <div key={cat.id} className="hc-category-card">
                    <div className="hc-card-header">
                      <h4>{cat.name}</h4>
                      <span className={`status-badge ${cat.isPublished ? 'published' : 'draft'}`}>
                        {cat.isPublished ? 'Published' : 'Draft'}
                      </span>
                    </div>
                    <p className="hc-card-description">{cat.description || cat.nameAr}</p>
                    <div className="hc-card-meta">
                      <span>{cat.articleCount || 0} articles</span>
                    </div>
                    <div className="hc-card-actions">
                      <button className="btn-icon" onClick={() => openEditCategory(cat)}>
                        <Icon icon={FiEdit2} />
                      </button>
                      <button className="btn-icon btn-delete" onClick={() => handleDeleteCategory(cat.id)}>
                        <Icon icon={FiTrash2} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="hc-section">
            <div className="hc-section-header">
              <div className="hc-search-box">
                <Icon icon={FiSearch} />
                <input
                  type="text"
                  placeholder="Search articles..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  onKeyPress={e => e.key === 'Enter' && loadData()}
                />
              </div>
              <button className="btn-primary" onClick={() => { resetArticleForm(); setShowArticleModal(true); }}>
                <Icon icon={FiPlus} /> Create Article
              </button>
            </div>

            {loading ? (
              <div className="loading-state">
                <Icon icon={FiRefreshCw} className="spinning" />
                <p>Loading articles...</p>
              </div>
            ) : articles.length === 0 ? (
              <div className="empty-state">
                <Icon icon={FiFileText} />
                <p>No articles found</p>
              </div>
            ) : (
              <>
                <div className="hc-articles-list">
                  {articles.map(article => (
                    <div key={article.id} className="hc-article-card">
                      <div className="hc-card-header">
                        <h4>{article.title}</h4>
                        <div className="hc-badges">
                          {article.isFeatured && <span className="badge-featured">Featured</span>}
                          <span className={`status-badge ${article.isPublished ? 'published' : 'draft'}`}>
                            {article.isPublished ? 'Published' : 'Draft'}
                          </span>
                        </div>
                      </div>
                      <p className="hc-card-summary">{article.summary || article.titleAr}</p>
                      <div className="hc-card-meta">
                        <span>{article.category?.name || 'No category'}</span>
                        <span>{article.views} views</span>
                      </div>
                      <div className="hc-card-actions">
                        <button className="btn-icon" onClick={() => { setSelectedArticle(article); setShowArticleView(true); }}>
                          <Icon icon={FiEye} />
                        </button>
                        <button className="btn-icon" onClick={() => openEditArticle(article)}>
                          <Icon icon={FiEdit2} />
                        </button>
                        <button className="btn-icon btn-delete" onClick={() => handleDeleteArticle(article.id)}>
                          <Icon icon={FiTrash2} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {totalPages > 1 && (
                  <div className="pagination">
                    <button onClick={() => setCurrentPage(p => Math.max(0, p - 1))} disabled={currentPage === 0}>
                      <Icon icon={FiChevronLeft} />
                    </button>
                    <span>Page {currentPage + 1} of {totalPages}</span>
                    <button onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))} disabled={currentPage >= totalPages - 1}>
                      <Icon icon={FiChevronRight} />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Category Modal */}
      {showCategoryModal && (
        <div className="modal-overlay" onClick={() => { setShowCategoryModal(false); resetCategoryForm(); }}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingCategory ? 'Edit Category' : 'Create Category'}</h3>
              <button className="btn-close" onClick={() => { setShowCategoryModal(false); resetCategoryForm(); }}>
                <Icon icon={FiX} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Name (English) *</label>
                <input
                  type="text"
                  value={categoryForm.name}
                  onChange={e => setCategoryForm({ ...categoryForm, name: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Name (Arabic) *</label>
                <input
                  type="text"
                  value={categoryForm.nameAr}
                  onChange={e => setCategoryForm({ ...categoryForm, nameAr: e.target.value })}
                  dir="rtl"
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={categoryForm.description}
                  onChange={e => setCategoryForm({ ...categoryForm, description: e.target.value })}
                  rows={3}
                />
              </div>
              <div className="form-group">
                <label>Description (Arabic)</label>
                <textarea
                  value={categoryForm.descriptionAr}
                  onChange={e => setCategoryForm({ ...categoryForm, descriptionAr: e.target.value })}
                  rows={3}
                  dir="rtl"
                />
              </div>
              <div className="form-group">
                <label>Order</label>
                <input
                  type="number"
                  value={categoryForm.order}
                  onChange={e => setCategoryForm({ ...categoryForm, order: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={categoryForm.isPublished}
                    onChange={e => setCategoryForm({ ...categoryForm, isPublished: e.target.checked })}
                  />
                  Published
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => { setShowCategoryModal(false); resetCategoryForm(); }}>
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={editingCategory ? handleUpdateCategory : handleCreateCategory}
                disabled={!categoryForm.name || !categoryForm.nameAr}
              >
                <Icon icon={FiCheck} />
                {editingCategory ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Article Modal */}
      {showArticleModal && (
        <div className="modal-overlay" onClick={() => { setShowArticleModal(false); resetArticleForm(); }}>
          <div className="modal modal-large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingArticle ? 'Edit Article' : 'Create Article'}</h3>
              <button className="btn-close" onClick={() => { setShowArticleModal(false); resetArticleForm(); }}>
                <Icon icon={FiX} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Title (English) *</label>
                <input
                  type="text"
                  value={articleForm.title}
                  onChange={e => setArticleForm({ ...articleForm, title: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Title (Arabic) *</label>
                <input
                  type="text"
                  value={articleForm.titleAr}
                  onChange={e => setArticleForm({ ...articleForm, titleAr: e.target.value })}
                  dir="rtl"
                />
              </div>
              <div className="form-group">
                <label>Category *</label>
                <select
                  value={articleForm.categoryId || ''}
                  onChange={e => {
                    const value = e.target.value;
                    const categoryId = value ? parseInt(value, 10) : 0;
                    console.log('Category selected:', value, 'Parsed as:', categoryId);
                    setArticleForm({ ...articleForm, categoryId });
                  }}
                  required
                >
                  <option value="">Select category *</option>
                  {categories.length === 0 ? (
                    <option value="" disabled>No categories available. Please create a category first.</option>
                  ) : (
                    categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))
                  )}
                </select>
              </div>
              <div className="form-group">
                <label>Summary</label>
                <textarea
                  value={articleForm.summary}
                  onChange={e => setArticleForm({ ...articleForm, summary: e.target.value })}
                  rows={2}
                />
              </div>
              <div className="form-group">
                <label>Content (HTML) *</label>
                <textarea
                  value={articleForm.content}
                  onChange={e => setArticleForm({ ...articleForm, content: e.target.value })}
                  rows={10}
                />
              </div>
              <div className="form-group">
                <label>Tags (comma-separated)</label>
                <input
                  type="text"
                  value={articleForm.tags}
                  onChange={e => setArticleForm({ ...articleForm, tags: e.target.value })}
                  placeholder="tag1, tag2, tag3"
                />
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={articleForm.isPublished}
                    onChange={e => setArticleForm({ ...articleForm, isPublished: e.target.checked })}
                  />
                  Published
                </label>
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={articleForm.isFeatured}
                    onChange={e => setArticleForm({ ...articleForm, isFeatured: e.target.checked })}
                  />
                  Featured
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => { setShowArticleModal(false); resetArticleForm(); }}>
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={editingArticle ? handleUpdateArticle : handleCreateArticle}
                disabled={
                  !articleForm.title || 
                  !articleForm.titleAr || 
                  !articleForm.content || 
                  !articleForm.categoryId || 
                  articleForm.categoryId === 0 ||
                  categories.length === 0
                }
              >
                <Icon icon={FiCheck} />
                {editingArticle ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Article View Modal */}
      {showArticleView && selectedArticle && (
        <div className="modal-overlay" onClick={() => { setShowArticleView(false); setSelectedArticle(null); }}>
          <div className="modal modal-large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{selectedArticle.title}</h3>
              <button className="btn-close" onClick={() => { setShowArticleView(false); setSelectedArticle(null); }}>
                <Icon icon={FiX} />
              </button>
            </div>
            <div className="modal-body">
              <div className="article-view">
                <div className="article-meta">
                  <span>Category: {selectedArticle.category?.name}</span>
                  <span>Views: {selectedArticle.views}</span>
                  {selectedArticle.isFeatured && <span className="badge-featured">Featured</span>}
                </div>
                <div className="article-content" dangerouslySetInnerHTML={{ __html: selectedArticle.content }} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => { setShowArticleView(false); setSelectedArticle(null); }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HelpCenter;

